import React, { useContext, useEffect, useState, type ReactNode } from 'react';
import { AuthContext } from './SuperadminContext';

export type AuthContextType = {
	currentAccount: Account | null;
	user: User | null;
	setUser: (user: User | null) => void;
	clearAuthData: () => void;
	setCurrentAccount: (account: Account) => void;
};

export const useSuperadminAuth = (): AuthContextType => {
	const context = useContext(AuthContext);

	if (context === undefined) {
		throw new Error('useSuperadminAuth must be used within a AuthProvider');
	}
	return context;
};

type AuthProviderProps = {
	defaultUser: User | null;
	defaultAccountID: number | null;
	children: ReactNode;
};

const TOKEN_NAME = import.meta.env.VITE_SUPERADMIN_TOKEN_NAME as string;

export const AuthProvider: React.FC<AuthProviderProps> = ({ defaultUser, defaultAccountID, children }) => {
	const [currentAccount, setCurrentAccount] = useState<Account | null>(
		defaultUser?.userAccounts?.find(userAccount => {
			return userAccount.accountID === defaultAccountID;
		})?.account || null,
	);

	const [user, setUser] = useState<User | null>(defaultUser);

	function clearAuthData(): void {
		setCurrentAccount(null);
		setUser(null);
		localStorage.removeItem('tavoittaja2AccountID');
		localStorage.removeItem(TOKEN_NAME);
	}

	useEffect(() => {
		if (user?.userAccounts && user.userAccounts.length === 1) {
			if (!user.userAccounts?.[0]?.account) {
				throw new Error('User accounts exist, but first one as default cannot be selected.');
			}
			setCurrentAccount(user.userAccounts?.[0].account);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const contextValue: AuthContextType = {
		currentAccount,
		user,
		setUser,
		clearAuthData,
		setCurrentAccount,
	};

	return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
